import { Link } from "react-router-dom";
import { formatHeat } from "@/utils/format";

export interface NewsCardItem {
  id: string;
  title: string;
  summary: string;
  channelName: string;
  dateLabel: string;
  readTime: number;
  heat: number;
  image: string;
  tags: string[];
}

interface NewsCardProps {
  article: NewsCardItem;
  variant?: "default" | "compact";
  index?: number;
}

export default function NewsCard({ article, variant = "default", index }: NewsCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/news/${article.id}`}
        className="group flex gap-4 py-4 border-b border-background-200 last:border-b-0 hover:bg-background-100/60 transition-colors rounded-md px-2 cursor-pointer"
      >
        {typeof index === "number" && (
          <span className="font-heading text-lg font-black text-primary-300 w-6 shrink-0 leading-6">
            {String(index + 1).padStart(2, "0")}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs font-label text-foreground-500">
            <span className="text-primary-600">{article.channelName}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {formatHeat(article.heat)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group h-full flex flex-col rounded-lg bg-background-50 border border-background-200 overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={`/news/${article.id}`} className="block w-full h-44 overflow-hidden cursor-pointer">
        <img
          src={article.image}
          alt={`${article.title} 相关配图`}
          title={`${article.title} - 瓜田速递`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex-1 flex flex-col p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-label font-medium">
            {article.channelName}
          </span>
          <span className="text-xs font-label text-foreground-400">{article.dateLabel}</span>
        </div>
        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug line-clamp-2">
          <Link to={`/news/${article.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs font-label text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true"></i>
            {article.readTime} 分钟读完
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
            {formatHeat(article.heat)}
          </span>
        </div>
      </div>
    </article>
  );
}